import { Container } from "components/shared";
import { Button, Skeleton, Spinner, Tooltip } from "components/ui";
import { CompanyContext } from "contexts/CompanyContext";
import { useContext, useEffect, useRef, useState } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { api as Api } from "api/http";
import MediaElement from "views/calendar/MediaElement";
import InputModern from "components/ui/Input/InputModern";
import useRandomContexts from "utils/hooks/useRandomContexts";
import { getAspectRatio } from "utils/getAspectRatio";
import { AIContext, aiSizeOptions } from "contexts/AIContext";
import { useWhitelabel } from "contexts/WhitelabelThemeContext";
import AIVideoDropdownSizes from "./components/AIVideoDropdownSizes";
import AIVideoDropdownModels from "./components/AIVideoDropdownModels";
import AIVideoGenerateButton from "./components/AIVideoGenerateButton";
import AIVideoImageInput from "./components/AIVideoImageInput";
import AIVideoInspirationDropdown from "./components/AIVideoInspirationDropdown";
import { BsClock, BsPlay, BsX } from "react-icons/bs";
import { HiOutlinePlus } from "react-icons/hi2";
import { ImageSelector } from "components/image-chat";
import AIVideoDropdownDuration from "./components/AIVideoDropdownnDuration";
import UpgradeBanner from "components/shared/UpgradeBanner";
import VideoCreditWarning from "components/shared/VideoCreditWarning";

const AIVideoBody = ({
    inputClassName = "",
    columnBreakPoints = { 0: 1, 768: 2, 1100: 3, 1400: 4 }, // 3 columns on xl, 2 on normal, 1 on tablet/mobile
    showPreview = true,
    handleOnSelect = undefined,
    handleOnUnselect = undefined,
    selectedVideos = undefined, 
    isSelectable = undefined,
    simplificated = false,
    showCreate = true
}) => {
    
    const { company, updateCompany } = useContext(CompanyContext);
    const { getDriverModel, getByQueryChat, getVideos, aiModels, aiVideoModels } = useContext(AIContext);
    const { generateRandomAIVideoPrompts } = useRandomContexts();
    const [ query, setQuery ] = useState("");
    const [ model, setModel ] = useState(getDriverModel("video")?.model || aiVideoModels?.[0]?.model);
    const [ size, setSize ] = useState("16:9"); // Default to 16:9 for videos
    const [ duration, setDuration ] = useState(8); // Default to 8 seconds
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isLoadingSuggestions, setIsLoadingSuggestions ] = useState(false);
    const [ isLoadingHistory, setIsLoadingHistory ] = useState(true);
    const [ historyVideos, setHistoryVideos ] = useState();
    const [ suggestions, setSuggestions ] = useState([]);
    const [ nextLink, setNextLink ] = useState();
    const [ selectedImage, setSelectedImage ] = useState(null); // For image input
    const [ pendingVideos, setPendingVideos ] = useState(new Set()); // Track pending video generations
    const [ pollingVideos, setPollingVideos ] = useState(new Set()); // Track videos currently being polled
    const [ showImageSelector, setShowImageSelector ] = useState(false);
    const inputRef = useRef();
    const pollingTimeouts = useRef(new Map()); // Track timeout references for cleanup
    const { whitelabel } = useWhitelabel();

    // Get current video model
    const currentVideoModel = aiVideoModels?.find(m => m.model === model);

    const getHistoryVideos = (replace = true) => {
        setIsLoadingHistory(true);
        Api.get(!replace && nextLink ? nextLink : `/aiResults?include=videos,videos.thumbnail,user,user.image&sort=-created_at&filter[company_id]=${company?.id}&filter[type]=video&page[size]=10`).then(({ data: results, links }) => {
            setHistoryVideos(replace ? results : [...historyVideos, ...results]);
            setNextLink(links?.next);
            
            // Check for pending videos and start polling for them
            const pending = results.filter(r => r.status === 'pending');
            if (pending.length > 0) {
                const newPendingIds = new Set([...pendingVideos, ...pending.map(p => p.id)]);
                setPendingVideos(newPendingIds);
                
                // Start polling for each pending video (avoid duplicate polling)
                pending.forEach(video => {
                    if (!pollingVideos.has(video.id)) {
                        setPollingVideos(prev => new Set([...prev, video.id]));
                        pollVideoStatus(video.id);
                    }
                });
            }
        }).catch((err) => {
            console.error(err);
        }).finally(() => {
            setIsLoadingHistory(false);
        });
    }

    const pollVideoStatus = async (videoId) => {
        let attempts = 0;
        const maxAttempts = 120; // 10 minutes max (120 * 5 seconds)
        const pollInterval = 10000; // 10 seconds
        let isPolling = true;
        
        const poll = async () => {
            if (!isPolling || attempts >= maxAttempts) return;
            
            attempts++;
            
            try {
                const pollResponse = await Api.get(`/aiResults/${videoId}`);
                const { status } = pollResponse.data;
                
                if (status === 'completed' || status === 'failed') {
                    isPolling = false;
                    
                    // Clean up timeout reference
                    pollingTimeouts.current.delete(videoId);
                    
                    setPendingVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                    
                    setPollingVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                    
                    // Refresh the history to show the completed video
                    getHistoryVideos();
                    return;
                }
                
                // Continue polling if still pending
                if (attempts < maxAttempts && isPolling) {
                    const timeoutId = setTimeout(poll, 5000);
                    pollingTimeouts.current.set(videoId, timeoutId);
                }
                
            } catch (err) {
                isPolling = false;
                
                // Clean up timeout reference
                pollingTimeouts.current.delete(videoId);
                
                // Remove from pending videos on error
                setPendingVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
                
                setPollingVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
            }
        };
        
        // Start the first poll after 5 seconds
        const timeoutId = setTimeout(poll, pollInterval);
        pollingTimeouts.current.set(videoId, timeoutId);
        
        // Set up cleanup for max attempts
        setTimeout(() => {
            if (attempts >= maxAttempts && isPolling) {
                isPolling = false;
                
                // Clean up timeout reference
                pollingTimeouts.current.delete(videoId);
                
                setPendingVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
                
                setPollingVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
            }
        }, maxAttempts * pollInterval);
    };

    const _removeSavedSuggestion = (_query) => {
        let savedSuggestions = suggestions;
        savedSuggestions = savedSuggestions.filter(suggestion => suggestion?.toLowerCase() != _query?.toLowerCase());
        setSuggestions(savedSuggestions);
        localStorage.setItem(`aiVideo.suggestions.${company?.id}`, JSON.stringify(savedSuggestions));
    }

    const generateVideo = async () => {
        let _query = query;
        _removeSavedSuggestion(_query);
        setIsLoading(true);
        
        const modelObj = aiVideoModels.find(e => e.model == model);
        if (modelObj?.needsTranslation) {
            _query = await getByQueryChat(`Translate this text to English or keep it the same if it's already in English: ${_query}
                Translated text: `, false, true, "");
        }
        
        try {
            const result = await getVideos(_query, size, model, selectedImage, false, undefined, duration);
            setQuery("");
            setSelectedImage(null);
            
            if (result.status === 'pending') {
                setPendingVideos(prev => new Set([...prev, result.id]));
                if (!pollingVideos.has(result.id)) {
                    setPollingVideos(prev => new Set([...prev, result.id]));
                    pollVideoStatus(result.id);
                }
            }
            
            getHistoryVideos();
        } catch (err) {
            console.error('Error generating video:', err);
        } finally {
            setIsLoading(false);
        }
    }

    const resize = () => {
      inputRef.current.style.cssText = 'height: 100px !important';
      inputRef.current.style.cssText = `height: ${Math.max(inputRef.current.scrollHeight, 0)}px !important`;
    }

    const updateCompanyVideoModel = async (_model) => {
        setModel(_model);
        const ai_preferences = {
            ...company?.ai_preferences,
            video_model: _model
        }
        await updateCompany({ ai_preferences });
    }

    const updateCompanyVideoAspectRatio = async (_aspectRatio) => {
        setSize(_aspectRatio);
        const ai_preferences = {
            ...company?.ai_preferences,
            video_aspect_ratio: _aspectRatio
        }
        await updateCompany({ ai_preferences });
    }

    const updateCompanyVideoDuration = async (_duration) => {
        setDuration(_duration);
        const ai_preferences = {
            ...company?.ai_preferences,
            video_duration: _duration
        }
        await updateCompany({ ai_preferences });
    }

    const handleSelectImages = (images) => {
        setSelectedImage(images?.[0]?.originalSrc);
        setShowImageSelector(false);
    }

    useEffect(() => {
        if (!!suggestions?.length) {
            localStorage.setItem(`aiVideo.suggestions.${company?.id}`, JSON.stringify(suggestions));
        }
    }, [suggestions]);

    useEffect(() => {
        getHistoryVideos();
    }, [company?.id]);
    
    useEffect(() => {
        const defaultModel = getDriverModel("video")?.model || aiVideoModels?.[0]?.model;
        setModel(defaultModel);
    }, [company?.id, company?.ai_preferences, aiVideoModels]);
  
    useEffect(() => {
      resize();
    }, [query]);

    useEffect(() => {
        if (!!model && !aiVideoModels?.find(m => m.model == model)?.availableSizes?.includes(size)) {
            const availableSizes = aiVideoModels?.find(m => m.model == model)?.availableSizes;
            if (availableSizes?.length > 0) {
                setSize(availableSizes[0]);
            }
        }
    }, [model]);

    useEffect(() => {
        return () => {
            pollingTimeouts.current.forEach(timeoutId => {
                clearTimeout(timeoutId);
            });
            pollingTimeouts.current.clear();
        };
    }, []);

    return (
        <div className={`w-full h-full ${simplificated ? 'flex flex-col' : 'flex flex-col'} gap-8`}>
            {/* Upgrade Banner */}
            <UpgradeBanner page="ai-video" />
            
            <div className={`flex flex-1 justify-center items-center ${!showCreate && `!hidden`}`}>
                
                {/* Revolutionary Centered Creation Studio */}
                <div className={`${simplificated ? 'flex-1' : 'flex-1'} flex justify-center relative z-[110] max-w-full w-full`}>
                    <div className="w-full mx-auto">
                        {/* Main Creation Card */}
                        <div className="relative group">
                            {/* Floating Background Elements */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-600 rounded-2xl blur-lg opacity-20 group-hover:opacity-30 transition-all duration-700 animate-pulse"></div>
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-500 via-blue-500 to-cyan-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-all duration-500"></div>
                            
                            {/* Main Card */}
                            <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-gray-700/30 shadow-2xl shadow-black/5 dark:shadow-black/20">
                                
                                {/* Header Section */}
                                <div className="relative p-6">
                                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-violet-500/10 via-blue-500/10 to-cyan-500/10 dark:from-violet-500/5 dark:via-blue-500/5 dark:to-cyan-500/5 rounded-t-2xl"></div>
                                    <div className="relative">
                                        <div className="flex items-center h-full gap-4">
                                            <div className="relative">
                                                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                                                    <BsPlay className="w-6 h-6 text-white ml-0.5" />
                                                </div>
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-cyan-400 rounded-full animate-ping"></div>
                                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-cyan-400 rounded-full"></div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                                                    AI Video Studio
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Transform your ideas into stunning videos
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Input Section */}
                                <div className="p-6 pb-0">
                                    <div className="relative">
                                        <InputModern
                                            fieldsetClassName="!bg-transparent !border-0"
                                            className="!bg-gradient-to-br from-gray-50/80 to-white/80 dark:!from-gray-800/80 dark:!to-gray-900/80 !border-0 !shadow-[inset_0_2px_8px_0_rgba(17,24,39,0.03),inset_0_-2px_8px_0_rgba(17,24,39,0.03),inset_2px_0_8px_0_rgba(17,24,39,0.03),inset_-2px_0_8px_0_rgba(17,24,39,0.03)] dark:!shadow-[inset_0_2px_8px_0_rgba(0,0,0,0.1),inset_0_-2px_8px_0_rgba(0,0,0,0.1),inset_2px_0_8px_0_rgba(0,0,0,0.1),inset_-2px_0_8px_0_rgba(0,0,0,0.1)] rounded-2xl !px-6 !py-5 !pb-14 min-h-[140px] max-h-[300px] overflow-auto resize-none !text-base placeholder:!text-gray-400 dark:placeholder:!text-gray-500 focus:!ring-0 focus:!shadow-lg focus:!shadow-violet-500/10 transition-all duration-300" 
                                            textArea={true}
                                            value={query}
                                            ref={inputRef}
                                            placeholder="Describe your vision... What story should this video tell?" 
                                            onChange={(e) => setQuery(e.target.value)} 
                                            onKeyUp={(e) => e.key == 'Enter' ? generateVideo() : void 0}
                                        />
                                        
                                        {/* Floating Controls */}
                                        <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                                            {/* Left side - Inspiration */}
                                            <div className="flex items-center gap-2">
                                                {/* Reference Image Button */}
                                                <Tooltip title="Add a reference image">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowImageSelector(true)}
                                                        className="relative w-8 h-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden flex items-center justify-center hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title={`Add a reference image`}
                                                        onMouseEnter={e => {
                                                            const overlay = e.currentTarget.querySelector('.image-selector-overlay');
                                                            if (overlay) overlay.style.opacity = 1;
                                                        }}
                                                        onMouseLeave={e => {
                                                            const overlay = e.currentTarget.querySelector('.image-selector-overlay');
                                                            if (overlay) overlay.style.opacity = 0;
                                                        }}
                                                    >
                                                        {selectedImage ? (
                                                            <>
                                                                <img src={selectedImage} alt="Reference" className="w-full h-full object-cover" />
                                                                <div
                                                                    className="image-selector-overlay transition-all duration-200 w-full h-full absolute top-0 left-0 bg-black/50 flex items-center justify-center"
                                                                    style={{ opacity: 0 }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedImage(null);
                                                                    }}
                                                                >
                                                                    <BsX className="w-3 h-3 text-white" />
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <HiOutlinePlus className="w-3 h-3 text-gray-400 dark:text-gray-500 hover:text-purple-500 dark:hover:text-purple-400 transition-colors" />
                                                        )}
                                                    </button>
                                                </Tooltip>
                                            </div>

                                            {/* Right side - Model and Size controls */}
                                            <div className="flex items-center gap-2">
                                                {/* Model Dropdown */}
                                                <AIVideoDropdownModels 
                                                    model={model}
                                                    setModel={updateCompanyVideoModel}
                                                    parentClassName="flex-1"
                                                    className="!bg-white/80 dark:!bg-gray-800/80 !border-white/50 dark:!border-gray-700/50 !backdrop-blur-sm rounded-lg !px-2 !py-2 !text-xs !min-w-[50px] !h-auto hover:!bg-white/90 dark:hover:!bg-gray-800/90 transition-all duration-300 !text-gray-600 dark:!text-gray-400 !shadow"
                                                    showCreditsInToggle={false}
                                                    truncate={true}
                                                />

                                                {/* Floating Aspect Ratio Dropdown */}
                                                <AIVideoDropdownSizes 
                                                    size={size}
                                                    setSize={updateCompanyVideoAspectRatio}
                                                    model={model}
                                                    parentClassName="relative"
                                                    className="!bg-white/80 dark:!bg-gray-800/80 !border-white/50 dark:!border-gray-700/50 !backdrop-blur-sm rounded-lg !px-1 !py-2 !text-xs !min-w-[50px] !h-auto hover:!bg-white/90 dark:hover:!bg-gray-800/90 transition-all duration-300 !text-gray-600 dark:!text-gray-400 !shadow"
                                                    disabled={!!selectedImage}
                                                />

                                                {/* Floating Duration Dropdown */}
                                                <AIVideoDropdownDuration 
                                                    duration={duration}
                                                    setDuration={updateCompanyVideoDuration}
                                                    model={model}
                                                    parentClassName="relative"
                                                    className="!bg-white/80 dark:!bg-gray-800/80 !border-white/50 dark:!border-gray-700/50 !backdrop-blur-sm rounded-lg !px-1 !py-2 !text-xs !min-w-[50px] !h-auto hover:!bg-white/90 dark:hover:!bg-gray-800/90 transition-all duration-300 !text-gray-600 dark:!text-gray-400 !shadow"
                                                />

                                                <AIVideoInspirationDropdown 
                                                    setQuery={setQuery}
                                                    selectedImage={selectedImage}
                                                    modelObj={currentVideoModel}
                                                    className="hover:!scale-105"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Generate Button */}
                                <div className="px-6 py-4 pb-6 space-y-3">
                                    <AIVideoGenerateButton
                                        isLoading={isLoading}
                                        query={query}
                                        generateVideo={generateVideo}
                                        model={model}
                                        duration={duration}
                                    />
                                    <VideoCreditWarning />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {(!!historyVideos?.length || isLoading || isLoadingHistory) && 
                <Container className={`relative ${simplificated ? 'flex-1' : 'flex-1 overflow-y-auto'} min-h-full`}>
                    <div className={`relative flex flex-col gap-8 shrink-0 max-w-full ${simplificated ? 'lg:max-w-full' : 'lg:max-w-[250px]'} max-h-[512px] w-full ${1 == 1 && `!max-w-none !w-full !max-h-none`}`}>
                        {isLoadingHistory && !isLoading && !historyVideos?.length
                            ? <ResponsiveMasonry columnsCountBreakPoints={columnBreakPoints} className={`w-full transition-all`}>
                                <Masonry gutter='0.75rem'>
                                    {[1,2,3,4,5,6].map(r => (
                                        <div key={r} className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse"></div>
                                    ))}
                                </Masonry>
                              </ResponsiveMasonry>
                            : <ResponsiveMasonry columnsCountBreakPoints={columnBreakPoints} className={`w-full transition-all`}>
                                <Masonry gutter='1.5rem'>
                                    {isLoading && 
                                        <div className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse flex items-center justify-center">
                                            <div className="text-center">
                                                <Spinner size={30} className="text-purple-600 mb-2" />
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Generating video...</p>
                                            </div>
                                        </div>
                                    }
                                    {historyVideos?.map((result, key) => {
                                        const isPending = pendingVideos.has(result.id) || result.status === 'pending' || result.status === 'processing';
                                        const video = result?.videos?.[0];
                                        const videoURL = result?.videos?.[0]?.url || result?.response?.split(",")?.[0];
                                        const videoThumbnailURL = result?.videos?.[0]?.thumbnail?.url || videoURL;
                                        
                                        if (isPending) {
                                            return (
                                                <div key={key} className="aspect-video bg-gray-300 dark:bg-gray-700 rounded-2xl animate-pulse flex items-center justify-center">
                                                    <div className="text-center">
                                                        <BsClock className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">Processing...</p>
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (!videoURL || result?.status === 'failed') {
                                            return null;
                                        }
                                        
                                        return (
                                            <span 
                                                key={key}
                                                className={`${!!showPreview && `transition-all overflow-hidden`} !rounded-2xl`}
                                            >
                                                <MediaElement 
                                                    key={videoURL}
                                                    src={videoThumbnailURL}
                                                    originalSrc={videoURL}
                                                    handleOnSelect={handleOnSelect}
                                                    handleOnUnselect={handleOnUnselect}
                                                    selected={!!selectedVideos?.find(e => e.originalSrc == videoURL)} 
                                                    isSelectable={isSelectable}
                                                    isEditable={false}
                                                    mediaDescription={`Prompt: "${result?.query}"`}
                                                    aiImageModel={result?.model}
                                                    user={result?.user}
                                                    isVideo={true}
                                                    onError={(e) => {
                                                        try { e.target.parentElement.parentElement.style = "display: none" } catch (err) {}
                                                    }}
                                                />
                                            </span>
                                        );
                                    })}
                                </Masonry>
                              </ResponsiveMasonry>
                        }
                        {!!nextLink && 
                            <div className="w-full flex justify-center mt-6">
                                <Button 
                                    variant="solid"
                                    onClick={() => getHistoryVideos(false)}
                                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                                >
                                    Load more
                                </Button>
                            </div>
                        }
                    </div>
                </Container>
            }

            <ImageSelector
                isOpen={showImageSelector}
                onClose={() => setShowImageSelector(false)}
                onSelect={handleSelectImages}
                maxImages={1}
                allowedTypes={["image"]}
            />
        </div>
    )
}

export default AIVideoBody;
